import React, { useState, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './Contact.css';

const CONTACT_METHODS = [
  {
    key: 'phone',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
      </svg>
    ),
    label: 'Phone Call',
    value: '+40 742 123 456',
    description: 'Available daily from 8:00 AM to 10:00 PM (Romania time)',
    href: 'tel:+40742123456',
    action: 'Call Now',
    color: 'green',
  },
  {
    key: 'whatsapp',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
    label: 'WhatsApp',
    value: '+40 742 123 456',
    description: 'Quick replies — usually within 30 minutes during daytime',
    href: 'https://wa.me/40742123456?text=Hello%20Cabana%20Buru!%20I%27d%20like%20to%20know%20more%20about%20your%20cabin.',
    action: 'Open Chat',
    color: 'whatsapp',
  },
  {
    key: 'email',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    label: 'Email',
    value: 'hello@cabanaburu.ro',
    description: 'We respond within 24 hours — perfect for detailed inquiries',
    href: 'mailto:hello@cabanaburu.ro?subject=Inquiry%20about%20Cabana%20Buru',
    action: 'Send Email',
    color: 'gold',
  },
];

const FAQ_ITEMS = [
  {
    q: 'What is the best time to visit?',
    a: 'Each season has its own magic. Summer is perfect for hiking and swimming, autumn for golden foliage, winter for snow and coziness, and spring for wildflower meadows.',
  },
  {
    q: 'How do I get to the cabin?',
    a: 'We\'re accessible by car via DN7A. The last 2km is on a forest road. We provide detailed directions and GPS coordinates upon booking. Airport pickup from Sibiu can be arranged.',
  },
  {
    q: 'Is there Wi-Fi available?',
    a: 'Yes, we have reliable Wi-Fi throughout the cabin. However, we encourage guests to disconnect and enjoy the mountain air!',
  },
  {
    q: 'Can I bring my pet?',
    a: 'We love furry guests! Dogs are welcome with prior notice. We have a fenced yard area and nearby trails perfect for walks.',
  },
  {
    q: 'What is the cancellation policy?',
    a: 'Free cancellation up to 7 days before check-in. Cancellations within 7 days receive a 50% refund. We understand plans change — just reach out!',
  },
];

export default function ContactPage() {
  const { t } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);
  const [copied, setCopied] = useState(null);
  const copyTimeoutRef = useRef(null);

  const translatedContactMethods = CONTACT_METHODS.map((method, index) => ({
    ...method,
    label: t(`contact.methods.list.${index}.label`),
    description: t(`contact.methods.list.${index}.description`),
    action: t(`contact.methods.list.${index}.action`),
  }));

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(null), 2000);
    });
  };

  const toggleFaq = (idx) => {
    setOpenFaq(prev => prev === idx ? null : idx);
  };

  return (
    <div className="ct page-enter">

      {/* ═══ HERO ═══ */}
      <section className="ct__hero">
        <div className="ct__hero-bg" aria-hidden="true">
          <div className="ct__hero-glow ct__hero-glow--1" />
          <div className="ct__hero-glow ct__hero-glow--2" />
          <div className="ct__hero-glow ct__hero-glow--3" />
          <div className="ct__hero-grid" />
          <div className="ct__hero-veil" />
        </div>

        {/* Floating decorative orbs */}
        <div className="ct__hero-orbs" aria-hidden="true">
          <div className="ct__hero-orb ct__hero-orb--1" />
          <div className="ct__hero-orb ct__hero-orb--2" />
          <div className="ct__hero-orb ct__hero-orb--3" />
        </div>

        {/* Animated corner frames */}
        <div className="ct__hero-frame ct__hero-frame--tl" aria-hidden="true" />
        <div className="ct__hero-frame ct__hero-frame--tr" aria-hidden="true" />
        <div className="ct__hero-frame ct__hero-frame--bl" aria-hidden="true" />
        <div className="ct__hero-frame ct__hero-frame--br" aria-hidden="true" />

        {/* Floating particles */}
        <div className="ct__hero-particles" aria-hidden="true">
          {[...Array(8)].map((_, i) => (
            <span key={i} className={`ct__hero-particle ct__hero-particle--${(i % 3) + 1}`}
              style={{
                left: `${10 + i * 11}%`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${6 + (i % 3) * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Horizontal scan line */}
        <div className="ct__hero-scanline" aria-hidden="true" />

        <div className="ct__hero-content ct__anim ct__anim--1">
          <div className="ct__hero-tagline">
            <span className="ct__hero-tagline-line" />
            <span className="ct__hero-tagline-text">{t('contact.hero.tagline')}</span>
            <span className="ct__hero-tagline-line" />
          </div>
          <h1 className="ct__hero-title">{t('contact.hero.title1')} <em>{t('contact.hero.title2')}</em></h1>
          <p className="ct__hero-sub">{t('contact.hero.sub')}</p>
        </div>
      </section>

      {/* ═══ CONTACT CARDS ═══ */}
      <section className="ct__methods">
        <div className="ct__methods-bg">
          <div className="ct__methods-orb ct__methods-orb--1" />
          <div className="ct__methods-orb ct__methods-orb--2" />
        </div>
        <div className="ct__methods-inner">
          <div className="ct__methods-header ct__anim ct__anim--1">
            <h2 className="ct__methods-title">{t('contact.methods.title1')} <em>{t('contact.methods.title2')}</em></h2>
            <p className="ct__methods-subtitle">{t('contact.methods.subtitle')}</p>
            <div className="ct__methods-title-line" />
          </div>

          <div className="ct__cards ct__anim ct__anim--2">
            {translatedContactMethods.map((method, idx) => (
              <article
                key={method.key}
                className={`ct__card ct__card--${method.color}`}
                style={{ animationDelay: `${idx * 0.12}s` }}
              >
                {/* Decorative glow */}
                <div className="ct__card-glow" />

                {/* Corner accents */}
                <div className="ct__card-corner ct__card-corner--tl" />
                <div className="ct__card-corner ct__card-corner--br" />

                {/* Icon */}
                <div className="ct__card-icon-wrap">
                  <div className="ct__card-icon">
                    {method.icon}
                  </div>
                  <div className="ct__card-icon-ring" />
                  <div className="ct__card-icon-pulse" />
                </div>

                {/* Content */}
                <h3 className="ct__card-label">{method.label}</h3>
                <div className="ct__card-value-row">
                  <span className="ct__card-value">{method.value}</span>
                  <button
                    className={`ct__card-copy${copied === method.key ? ' ct__card-copy--done' : ''}`}
                    onClick={() => handleCopy(method.value, method.key)}
                    title="Copy to clipboard"
                  >
                    {copied === method.key ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="ct__card-desc">{method.description}</p>

                {/* CTA */}
                <a href={method.href} className="ct__card-btn" target={method.key === 'whatsapp' ? '_blank' : undefined} rel={method.key === 'whatsapp' ? 'noopener noreferrer' : undefined}>
                  <span className="ct__card-btn-text">{method.action}</span>
                  <span className="ct__card-btn-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INFO STRIP ═══ */}
      <section className="ct__info ct__anim ct__anim--1">
        <div className="ct__info-inner">
          <div className="ct__info-item">
            <div className="ct__info-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div>
              <span className="ct__info-label">{t('contact.info.responseTime.label')}</span>
              <span className="ct__info-value">{t('contact.info.responseTime.value')}</span>
            </div>
          </div>
          <div className="ct__info-divider" />
          <div className="ct__info-item">
            <div className="ct__info-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <div>
              <span className="ct__info-label">{t('contact.info.location.label')}</span>
              <span className="ct__info-value">{t('contact.info.location.value')}</span>
            </div>
          </div>
          <div className="ct__info-divider" />
          <div className="ct__info-item">
            <div className="ct__info-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div>
              <span className="ct__info-label">{t('contact.info.availability.label')}</span>
              <span className="ct__info-value">{t('contact.info.availability.value')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ MAP / LOCATION ═══ */}
      <section className="ct__location ct__anim ct__anim--2">
        <div className="ct__location-inner">
          <div className="ct__location-text">
            <h2 className="ct__location-title">{t('contact.location.title1')} <em>{t('contact.location.title2')}</em></h2>
            <p className="ct__location-desc">
              {t('contact.location.desc')}
            </p>
            <div className="ct__location-details">
              <div className="ct__location-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <div>
                  <strong>{t('contact.mapDetails.address.label')}</strong>
                  <span>{t('contact.mapDetails.address.value')}</span>
                </div>
              </div>
              <div className="ct__location-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
                <div>
                  <strong>{t('contact.mapDetails.coords.label')}</strong>
                  <span>{t('contact.mapDetails.coords.value')}</span>
                </div>
              </div>
              <div className="ct__location-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
                <div>
                  <strong>{t('contact.mapDetails.note.label')}</strong>
                  <span>{t('contact.mapDetails.note.value')}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="ct__location-map">
            <div className="ct__location-map-wrapper" style={{ position: 'relative', width: '100%', padding: '0 20px 20px 0' }}>
              {/* Decorative background square for 3D effect */}
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                width: 'calc(100% - 20px)',
                height: 'calc(100% - 20px)',
                backgroundColor: 'rgba(212, 175, 55, 0.08)',
                border: '1px solid rgba(212, 175, 55, 0.2)',
                borderRadius: '20px',
                zIndex: 0
              }} />
              
              <div className="ct__location-map-frame" style={{
                position: 'relative',
                zIndex: 1,
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <iframe 
                  src="https://maps.google.com/maps?q=46.508820044690935,23.60090541972052&t=k&z=14&ie=UTF8&iwloc=&output=embed"
                  width="100%" 
                  height="100%" 
                  style={{ border: 0, minHeight: '400px', display: 'block' }} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps Cabana Piatra Buru"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ SECTION ═══ */}
      <section className="ct__faq">
        <div className="ct__faq-inner">
          <div className="ct__faq-header ct__anim ct__anim--1">
            <h2 className="ct__faq-title">{t('contact.faq.title1')} <em>{t('contact.faq.title2')}</em></h2>
            <p className="ct__faq-subtitle">{t('contact.faq.sub')}</p>
            <div className="ct__faq-title-line" />
          </div>

          <div className="ct__faq-list ct__anim ct__anim--2">
            {t('contact.faq.list', { returnObjects: true }).map((item, idx) => (
              <div
                key={idx}
                className={`ct__faq-item${openFaq === idx ? ' ct__faq-item--open' : ''}`}
              >
                <button className="ct__faq-q" onClick={() => toggleFaq(idx)}>
                  <span className="ct__faq-q-text">{item.q}</span>
                  <span className="ct__faq-q-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </span>
                </button>
                <div className="ct__faq-a-wrap">
                  <p className="ct__faq-a">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ BOTTOM CTA ═══ */}
      <section className="ct__bottom ct__anim ct__anim--3">
        <div className="ct__bottom-bg">
          <div className="ct__bottom-orb ct__bottom-orb--1" />
          <div className="ct__bottom-orb ct__bottom-orb--2" />
        </div>
        <div className="ct__bottom-inner">
          <div className="ct__bottom-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
          </div>
          <h3>{t('contact.bottom.title')}</h3>
          <p>{t('contact.bottom.desc')}</p>
          <div className="ct__bottom-actions">
            <a href="https://wa.me/40742123456" className="ct__bottom-btn ct__bottom-btn--primary" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              {t('contact.bottom.whatsapp')}
            </a>
            <a href="tel:+40742123456" className="ct__bottom-btn ct__bottom-btn--outline">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
              {t('contact.bottom.call')}
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
